import { ColorResult } from 'react-color';

import yup from 'utils/yup';

import { accrualMethods } from '../../shared/options';

export const initialCategoryFormData = {
  id: '',
  name: '',
  description: '',
  is_paid: 'false',
  in_hours: 'false',
  active: true,
  users: [] as Array<any>,
  group_ids: [],
  color: undefined as ColorResult | undefined,

  // CRON EXPRESSION GENERATOR FIELDS
  accrue_per_period: '0', // Hours or Days
  accrue_type: accrualMethods.find(method => method.value === 'Daily'), // Daily, weekly, monthly, quarterly, twice-a-year, on-anniversary
  accrue_period: '0 0 * * *', //Cron Expression
  totalAccrualDays: 365, // Total accrual days

  //Fields for generating cron expression
  accrue_hourly: '0' as any, // for hourly
  accrue_week_day: '' as any, // for weekly
  accrue_month_day_one: '' as any, // for twice-a-month and monthly
  accrue_month_day_two: '' as any, // for twice-a-month
  accrue_year_day: '' as any, // for quartely and twice-a-year day
  accrue_year_month_one: '' as any, // for quarterly and twice-a-year month
  accrue_year_month_two: '' as any, // for quarterly and twice-a-year month
  accrue_year_month_three: '' as any, // for quarterly month
  accrue_year_month_four: '' as any, // for quarterly month

  // Waiting period
  set_waiting_period: '0' as any,
  accrue_start: null as Date | null,
  // waiting_period: '' as any,
  // waiting_period_type: '' as any,

  // Employee Accrual Balance Cap
  set_max_balance: '0',
  max_balance: '',

  // Carryover Expiration
  reset_balance_yearly: '1',
  carryover_expiration_type: null as ILabelValueOptions | null,
  max_carryover_balance: '',

  allow_negative_balance: false,

  // Leave Duration
  max_request_duration: '',
  min_request_duration: '',

  initial_balance: 0,
};

export const categoryValidationSchema = yup.object().shape({
  name: yup.string().required(),
  accrue_per_period: yup.number().moreThan(-1).required(),
  accrue_type: yup
    .object({
      label: yup.string().required(),
      value: yup.string().required(),
    })
    .required(),

  accrue_hourly: yup.number().when('accrue_type', {
    is: (accrue_type: any) => accrue_type?.value === 'Hourly',
    then: yup.number().moreThan(0).required(),
    otherwise: yup.number().notRequired(),
  }),

  accrue_week_day: yup.mixed().when('accrue_type', {
    is: (accrue_type: any) => accrue_type?.value === 'Weekly',
    then: yup.mixed().required(),
    otherwise: yup.mixed().notRequired(),
  }),

  accrue_month_day_one: yup.mixed().when('accrue_type', {
    is: (type: any) => ['Monthly', 'Bi-Monthly'].includes(type?.value),
    then: yup.mixed().required(),
    otherwise: yup.mixed().notRequired(),
  }),

  accrue_month_day_two: yup.mixed().when('accrue_type', {
    is: (accrue_type: any) => accrue_type?.value === 'Bi-Monthly',
    then: yup
      .mixed()
      .required()
      .test('not-same-as-day-one', { msg: 'Day Two must be different from Day One' }, function (value) {
        const { accrue_month_day_one } = this.parent;
        if (!value || !accrue_month_day_one) return true;
        return value.value !== accrue_month_day_one.value;
      }),
  }),

  accrue_year_day: yup.mixed().when('accrue_type', {
    is: (type: any) => ['Annually', 'Quarterly', 'Semi-Annually'].includes(type?.value),
    then: yup.mixed().required(),
    otherwise: yup.mixed().notRequired(),
  }),

  accrue_year_month_one: yup.mixed().when('accrue_type', {
    is: (type: any) => ['Quarterly', 'Semi-Annually', 'Annually'].includes(type?.value),
    then: yup.mixed().required(),
    otherwise: yup.mixed().notRequired(),
  }),

  accrue_year_month_two: yup.mixed().when('accrue_type', {
    is: (type: any) => ['Quarterly', 'Semi-Annually'].includes(type?.value),
    then: yup
      .mixed()
      .required()
      .test('unique-months-1-2', { msg: 'Month 1 and Month 2 must be different' }, function (value) {
        const { accrue_type, accrue_year_month_one } = this.parent;
        if (accrue_type?.value !== 'Semi-Annually') return true;
        if (!value || !accrue_year_month_one) return true;
        return value.value !== accrue_year_month_one.value;
      }),
    otherwise: yup.mixed().notRequired(),
  }),

  accrue_year_month_three: yup.mixed().when('accrue_type', {
    is: (type: any) => type?.value === 'Quarterly',
    then: yup.mixed().required(),
    otherwise: yup.mixed().notRequired(),
  }),

  accrue_year_month_four: yup.mixed().when('accrue_type', {
    is: (type: any) => type?.value === 'Quarterly',
    then: yup
      .mixed()
      .required()
      .test('unique-months-quarterly', { msg: 'All selected months must be unique' }, function (value) {
        const { accrue_type, accrue_year_month_one, accrue_year_month_two, accrue_year_month_three } = this.parent;

        if (accrue_type?.value !== 'Quarterly') return true;

        const all = [
          accrue_year_month_one?.value,
          accrue_year_month_two?.value,
          accrue_year_month_three?.value,
          value?.value,
        ].filter(Boolean);

        const unique = new Set(all);
        return unique.size === all.length;
      }),
    otherwise: yup.mixed().notRequired(),
  }),

  accrue_start: yup.date().when('set_waiting_period', {
    is: (val: any) => val === '1',
    then: yup.date().required().typeError({ msg: 'This must be a valid date' }),
    otherwise: yup.date().nullable(),
  }),
  max_balance: yup.number().when('set_max_balance', {
    is: (val: any) => val === '1',
    then: yup.number().required(),
    otherwise: yup.number().nullable(),
  }),
  carryover_expiration_type: yup.mixed().when('allow_future_year_entries', {
    is: (val: any) => val === '1',
    then: yup.mixed().required(),
    otherwise: yup.mixed().nullable(),
  }),
  max_carryover_balance: yup.number().when(['allow_future_year_entries', 'carryover_expiration_type'], {
    is: (allow_future_year_entries: any, carryover_expiration_type: any) =>
      allow_future_year_entries === '1' &&
      ((carryover_expiration_type && carryover_expiration_type.value === 'up-to') ||
        carryover_expiration_type === 'up-to'),
    then: yup.number().moreThan(0).required(),
    otherwise: yup.number().nullable(),
  }),

  max_request_duration: yup.number(),
  min_request_duration: yup.number(),

  initial_balance: yup.number(),
});
